import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Plus, Key, UserX, UserCheck, Building2, Shield, MapPin, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { isSuperAdmin } from "@/lib/api";
import BottomNav, { Sidebar } from "@/components/Navigation";
import { useConfirmDialog } from "@/components/ConfirmDialog";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "instructor", label: "Instructor" },
];

// Display-only placeholder shown in the "create / reset password" form.
// The admin always types their own value over this before submitting — it is
// NOT a real credential. Centralised here so it's easy to find/change.
const DEFAULT_TEMP_PASSWORD_HINT = "ChangeMe@123";

const UserManagement = ({ user, onLogout }) => {
  const { confirm, dialog: confirmDialog } = useConfirmDialog();
  const [users, setUsers] = useState([]);
  const [studios, setStudios] = useState([]);
  const [instructorByUserId, setInstructorByUserId] = useState({});
  const [roleFilter, setRoleFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [tempPassword, setTempPassword] = useState("");

  // Phase-2: instructor permissions panel
  const [permsUserId, setPermsUserId] = useState(null);
  const [permsForm, setPermsForm] = useState(null); // {attendance_access, calendar_access, payment_visibility}
  const [permsSaving, setPermsSaving] = useState(false);

  // Apr 2026 — Edit Studio Access dialog.
  // {user, selected, orphanImpact?, saving} — orphanImpact is set when the
  // backend returns 409 due to active operations in a removed studio.
  const [studiosEditor, setStudiosEditor] = useState(null);
  const meIsSuper = isSuperAdmin(user);

  // RBAC mirror of backend rule: super-admin can edit anyone; non-super
  // admin cannot edit super-admins or other admins. Used to hide the
  // "Edit Studios" button on rows that would 403.
  const canEditStudiosFor = (target) => {
    if (!target) return false;
    if (target.user_id === user?.user_id) return false;  // not your own scope
    if (meIsSuper) return true;
    const isSuperTarget = target.is_super_admin || target.role === "super_admin";
    if (isSuperTarget) return false;
    if (target.role === "admin") return false;
    return true;
  };

  const openStudiosEditor = (u) => {
    setStudiosEditor({
      user: u,
      selected: [...(u.studio_assignments || [])],
      orphanImpact: null,
      saving: false,
    });
  };

  const toggleStudioSelection = (sid) => {
    setStudiosEditor((cur) => {
      if (!cur) return cur;
      const has = cur.selected.includes(sid);
      return { ...cur, selected: has ? cur.selected.filter((s) => s !== sid) : [...cur.selected, sid] };
    });
  };

  const saveStudiosEdit = async (acknowledgeOrphan = false) => {
    if (!studiosEditor) return;
    const u = studiosEditor.user;
    const isInstructor = u.role === "instructor";
    const isAdminActive = (u.role === "admin" || u.role === "instructor") && u.is_active !== false;
    if (studiosEditor.selected.length === 0 && isAdminActive) {
      toast.error("At least one studio access is required.");
      return;
    }
    setStudiosEditor((c) => ({ ...c, saving: true }));
    try {
      await api.put(`/users/${u.user_id}`, {
        studio_assignments: studiosEditor.selected,
        ...(acknowledgeOrphan ? { acknowledge_orphan: true } : {}),
      });
      toast.success(`Studio access updated for ${u.name}`);
      setStudiosEditor(null);
      loadUsers();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // 409 with structured payload → show orphan acknowledgement screen.
      if (e?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "studio_access_orphan") {
        setStudiosEditor((c) => ({ ...c, saving: false, orphanImpact: detail.impact || [] }));
        return;
      }
      const msg = typeof detail === "string" ? detail : (detail?.message || "Failed to update studio access");
      toast.error(msg);
      setStudiosEditor((c) => ({ ...c, saving: false }));
    }
  };

  const openPermissions = async (uid) => {
    setPermsUserId(uid);
    setPermsForm(null);
    try {
      const res = await api.get(`/users/${uid}/permissions`);
      setPermsForm(res.data.permissions);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load permissions");
      setPermsUserId(null);
    }
  };

  const savePermissions = async () => {
    if (!permsUserId || !permsForm) return;
    setPermsSaving(true);
    try {
      await api.patch(`/users/${permsUserId}/permissions`, permsForm);
      toast.success("Permissions updated");
      setPermsUserId(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update permissions");
    } finally {
      setPermsSaving(false);
    }
  };

  const [form, setForm] = useState({
    name: "", phone: "", email: "", role: "instructor",
    temp_password: DEFAULT_TEMP_PASSWORD_HINT, studio_assignments: [],
  });

  const loadUsers = async () => {
    try {
      const [adminRes, instRes] = await Promise.all([
        api.get("/users", { params: { role: "admin" } }),
        api.get("/users", { params: { role: "instructor" } }),
      ]);
      const merged = [
        ...(adminRes.data.users || []),
        ...(instRes.data.users || []),
      ];
      setUsers(merged);
    } catch (e) {
      toast.error("Failed to load users");
    }
  };

  useEffect(() => {
    loadUsers();
    api.get("/studios").then((r) => setStudios(r.data.studios || []));
    api.get("/instructors").then((r) => {
      const map = {};
      (r.data.instructors || []).forEach((i) => { if (i.user_id) map[i.user_id] = i; });
      setInstructorByUserId(map);
    }).catch(() => {});
  }, []);

  const filtered = users.filter((u) => roleFilter === "all" || u.role === roleFilter);

  const toggleStudioAssignment = (sid) => {
    const current = form.studio_assignments || [];
    setForm({
      ...form,
      studio_assignments: current.includes(sid)
        ? current.filter((x) => x !== sid)
        : [...current, sid],
    });
  };

  const handleCreate = async () => {
    if (!form.name || !form.phone || !form.role) {
      toast.error("Name, phone, role are required");
      return;
    }
    if (form.role !== "student" && !form.temp_password) {
      toast.error("Temporary password is required");
      return;
    }
    try {
      await api.post("/users/create", form);
      toast.success("User created");
      setCreateOpen(false);
      setForm({ name: "", phone: "", email: "", role: "instructor", temp_password: DEFAULT_TEMP_PASSWORD_HINT, studio_assignments: [] });
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create user");
    }
  };

  const handleDeactivate = async (u) => {
    const ok = await confirm({
      title: "Deactivate this user?",
      message: `${u.name} will lose access immediately. You can reactivate them at any time from this list.`,
      confirmText: "Deactivate",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.put(`/users/${u.user_id}/deactivate`);
      toast.success("User deactivated");
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleReactivate = async (u) => {
    try {
      await api.put(`/users/${u.user_id}/reactivate`);
      toast.success("User reactivated");
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleResetPassword = async () => {
    if (!tempPassword || tempPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.post("/auth/admin-reset-password", { user_id: resetUserId, temp_password: tempPassword });
      toast.success(`Password reset. Temporary: ${tempPassword}`);
      setResetUserId(null);
      setTempPassword("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 md:ml-64 p-4 md:p-8 pb-24 md:pb-8">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-5xl mx-auto space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-violet-600" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900">User Access</h1>
                <p className="text-sm text-slate-500 mt-0.5">Manage admins, instructors and their studio access.</p>
              </div>
            </div>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-user-button" className="bg-violet-600 hover:bg-violet-700 rounded-full">
                  <Plus className="w-4 h-4 mr-2" /> New User
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New User</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <Field label="Full Name">
                    <Input data-testid="new-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </Field>
                  <Field label="Mobile Number">
                    <Input data-testid="new-user-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} />
                  </Field>
                  <Field label="Email (optional)">
                    <Input data-testid="new-user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </Field>
                  <Field label="Role">
                    <select
                      data-testid="new-user-role"
                      value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}
                      className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                    >
                      {ROLES.filter((r) => r.value !== "admin" || isSuperAdmin(user)).map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Temporary Password">
                    <Input data-testid="new-user-password" value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })} />
                    <p className="text-xs text-slate-500 mt-1">User will be forced to change on first login.</p>
                  </Field>
                  <Field label="Studio Access">
                    <div className="space-y-1.5 max-h-44 overflow-y-auto border border-slate-200 rounded-xl p-2">
                      {studios.length === 0 && <p className="text-xs text-slate-500">No studios available</p>}
                      {studios.map((s) => (
                        <label key={s.studio_id} className="flex items-center gap-2 cursor-pointer text-sm py-1">
                          <input
                            data-testid={`studio-cb-${s.studio_id}`}
                            type="checkbox"
                            checked={(form.studio_assignments || []).includes(s.studio_id)}
                            onChange={() => toggleStudioAssignment(s.studio_id)}
                          />
                          <Building2 className="w-3.5 h-3.5 text-slate-400" />
                          <span>{s.name}</span>
                        </label>
                      ))}
                    </div>
                  </Field>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                  <Button data-testid="submit-new-user" onClick={handleCreate} className="bg-violet-600 hover:bg-violet-700">Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-2">
            {[{ k: "all", l: "All" }, { k: "admin", l: "Admins" }, { k: "instructor", l: "Instructors" }].map((f) => (
              <button
                key={f.k}
                data-testid={`role-filter-${f.k}`}
                onClick={() => setRoleFilter(f.k)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${roleFilter === f.k ? "bg-violet-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-violet-300"}`}
              >
                {f.l}
              </button>
            ))}
          </div>

          {/* Users list */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                <tr>
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Phone</th>
                  <th className="text-left p-3">Role</th>
                  <th className="text-left p-3">Studios</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan="6" className="p-6 text-center text-slate-500">No users found</td></tr>
                )}
                {filtered.map((u) => (
                  <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-t border-slate-100">
                    <td className="p-3 font-medium text-slate-800">{u.name}</td>
                    <td className="p-3 text-slate-600">{u.phone}</td>
                    <td className="p-3 capitalize text-slate-700">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span>{u.role?.replace("_", " ")}</span>
                        {u.role === "instructor" && instructorByUserId[u.user_id] && (
                          <span data-testid={`user-instructor-link-${u.user_id}`} title="Instructor profile linked" className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-200">
                            Instructor profile
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-xs text-slate-500">
                      {(u.studio_assignments || []).length === 0 ? "—" : `${(u.studio_assignments || []).length} assigned`}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.is_active === false ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                        {u.is_active === false ? "Inactive" : "Active"}
                      </span>
                    </td>
                    <td className="p-3 text-right space-x-2">
                      {canEditStudiosFor(u) && (
                        <Button
                          data-testid={`edit-studios-${u.user_id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => openStudiosEditor(u)}
                          className="text-emerald-700 hover:bg-emerald-50"
                        >
                          <MapPin className="w-3.5 h-3.5 mr-1" /> Studios
                        </Button>
                      )}
                      {u.role === "instructor" && (
                        <Button
                          data-testid={`perms-${u.user_id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => openPermissions(u.user_id)}
                          className="text-violet-700 hover:bg-violet-50"
                        >
                          <Shield className="w-3.5 h-3.5 mr-1" /> Permissions
                        </Button>
                      )}
                      <Button data-testid={`reset-pw-${u.user_id}`} size="sm" variant="outline" onClick={() => { setResetUserId(u.user_id); setTempPassword(DEFAULT_TEMP_PASSWORD_HINT); }}>
                        <Key className="w-3.5 h-3.5 mr-1" /> Reset
                      </Button>
                      {u.is_active === false ? (
                        <Button data-testid={`reactivate-${u.user_id}`} size="sm" variant="outline" onClick={() => handleReactivate(u)} className="text-emerald-700 hover:bg-emerald-50">
                          <UserCheck className="w-3.5 h-3.5 mr-1" /> Activate
                        </Button>
                      ) : (
                        <Button data-testid={`deactivate-${u.user_id}`} size="sm" variant="outline" onClick={() => handleDeactivate(u)} className="text-red-600 hover:bg-red-50">
                          <UserX className="w-3.5 h-3.5 mr-1" /> Deactivate
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </main>
      <BottomNav user={user} onLogout={onLogout} />
      {confirmDialog}

      {/* Reset Password Modal */}
      <Dialog open={!!resetUserId} onOpenChange={(o) => !o && setResetUserId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <Field label="Temporary Password">
            <Input data-testid="reset-temp-pw" value={tempPassword} onChange={(e) => setTempPassword(e.target.value)} />
            <p className="text-xs text-slate-500 mt-1">User must change on next login.</p>
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetUserId(null)}>Cancel</Button>
            <Button data-testid="confirm-reset-pw" onClick={handleResetPassword} className="bg-violet-600 hover:bg-violet-700">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apr 2026 — Edit Studio Access Modal */}
      <Dialog open={!!studiosEditor} onOpenChange={(o) => !o && setStudiosEditor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-emerald-600" />
              {studiosEditor?.orphanImpact ? "Confirm Studio Access Removal" : `Studio Access — ${studiosEditor?.user?.name || ""}`}
            </DialogTitle>
          </DialogHeader>
          {studiosEditor && !studiosEditor.orphanImpact && (
            <>
              <p className="text-sm text-slate-600 mb-3">
                Choose which studios this {studiosEditor.user.role === "instructor" ? "instructor" : "user"} can access. Changes apply immediately.
              </p>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {studios.length === 0 && (
                  <p className="text-sm text-slate-500">No studios configured.</p>
                )}
                {studios.map((s) => (
                  <label
                    key={s.studio_id}
                    data-testid={`studio-toggle-${s.studio_id}`}
                    className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={studiosEditor.selected.includes(s.studio_id)}
                      onChange={() => toggleStudioSelection(s.studio_id)}
                      className="w-4 h-4 accent-emerald-600"
                    />
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-medium text-slate-800">{s.name}</span>
                  </label>
                ))}
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setStudiosEditor(null)}>Cancel</Button>
                <Button
                  data-testid="save-studio-access"
                  onClick={() => saveStudiosEdit(false)}
                  disabled={studiosEditor.saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {studiosEditor.saving ? "Saving…" : "Save"}
                </Button>
              </DialogFooter>
            </>
          )}
          {studiosEditor?.orphanImpact && (
            <>
              <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900">
                <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-amber-600" />
                <div className="text-sm leading-relaxed">
                  <p className="font-semibold">
                    {studiosEditor.user.name} has active operations in the studio(s) being removed.
                  </p>
                  <p className="mt-1">
                    Removing access may impact visibility and operations.
                  </p>
                </div>
              </div>
              <ul className="mt-3 space-y-2 text-sm">
                {studiosEditor.orphanImpact.map((i) => (
                  <li key={i.studio_id} className="p-2.5 rounded-lg border border-amber-200 bg-amber-50/40">
                    <p className="font-semibold text-slate-800">{i.studio_name}</p>
                    <p className="text-xs text-slate-600 mt-0.5">
                      {i.active_batches > 0 && <>{i.active_batches} active batch{i.active_batches > 1 ? "es" : ""} · </>}
                      {i.upcoming_sessions > 0 && <>{i.upcoming_sessions} upcoming session{i.upcoming_sessions > 1 ? "s" : ""}</>}
                      {i.active_batches === 0 && i.upcoming_sessions === 0 && "Other operational records"}
                    </p>
                  </li>
                ))}
              </ul>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setStudiosEditor((c) => ({ ...c, orphanImpact: null }))}>
                  Back
                </Button>
                <Button
                  data-testid="confirm-orphan-removal"
                  onClick={() => saveStudiosEdit(true)}
                  disabled={studiosEditor.saving}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {studiosEditor.saving ? "Saving…" : "Continue Anyway"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase-2: Instructor Permissions Modal */}
      <Dialog open={!!permsUserId} onOpenChange={(o) => !o && setPermsUserId(null)}>
        <DialogContent className="max-w-md" data-testid="perms-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-violet-600" /> Instructor Permissions
            </DialogTitle>
          </DialogHeader>
          {!permsForm ? (
            <p className="text-sm text-slate-500 py-6 text-center">Loading…</p>
          ) : (
            <div className="space-y-4">
              <div className="rounded-xl bg-violet-50/60 border border-violet-100 px-3 py-2 text-xs text-violet-800">
                Admins always have full access. These toggles only affect <strong>instructor users</strong>. Changes take effect immediately on the instructor's next request.
              </div>

              {/* Attendance */}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Attendance Access</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { v: "enabled", label: "Enabled", desc: "Can mark & submit attendance for assigned batches" },
                    { v: "disabled", label: "Disabled", desc: "Hidden from attendance flow entirely" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`perm-attendance-${opt.v}`}
                      onClick={() => setPermsForm({ ...permsForm, attendance_access: opt.v })}
                      className={`text-left px-3 py-2 rounded-xl border text-xs transition ${permsForm.attendance_access === opt.v ? "border-violet-500 bg-violet-50" : "border-slate-200 bg-white hover:border-violet-200"}`}
                    >
                      <p className="font-semibold text-slate-800">{opt.label}</p>
                      <p className="text-slate-500 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Calendar */}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Calendar Access</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { v: "none", label: "No Access", desc: "Calendar fully hidden" },
                    { v: "view", label: "View", desc: "Own events only" },
                    { v: "view_with_details", label: "View + Details", desc: "All events, read-only" },
                    { v: "edit", label: "View + Edit", desc: "Manage bookings" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`perm-calendar-${opt.v}`}
                      onClick={() => setPermsForm({ ...permsForm, calendar_access: opt.v })}
                      className={`text-left px-3 py-2 rounded-xl border text-xs transition ${permsForm.calendar_access === opt.v ? "border-violet-500 bg-violet-50" : "border-slate-200 bg-white hover:border-violet-200"}`}
                    >
                      <p className="font-semibold text-slate-800">{opt.label}</p>
                      <p className="text-slate-500 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment Visibility */}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Payment Visibility</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { v: "enabled", label: "Enabled", desc: "Sees outstanding & status of assigned batches' students" },
                    { v: "disabled", label: "Disabled", desc: "No access to payment data" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`perm-payment-${opt.v}`}
                      onClick={() => setPermsForm({ ...permsForm, payment_visibility: opt.v })}
                      className={`text-left px-3 py-2 rounded-xl border text-xs transition ${permsForm.payment_visibility === opt.v ? "border-violet-500 bg-violet-50" : "border-slate-200 bg-white hover:border-violet-200"}`}
                    >
                      <p className="font-semibold text-slate-800">{opt.label}</p>
                      <p className="text-slate-500 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Phase-D: Batch Edit Access */}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Batch Edit Access</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { v: "enabled", label: "Enabled", desc: "Can edit any batch/session in their assigned studios (room, timing, instructor)" },
                    { v: "disabled", label: "Disabled", desc: "Cannot edit batches or sessions" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`perm-batch-edit-${opt.v}`}
                      onClick={() => setPermsForm({ ...permsForm, batch_edit_access: opt.v })}
                      className={`text-left px-3 py-2 rounded-xl border text-xs transition ${permsForm.batch_edit_access === opt.v ? "border-violet-500 bg-violet-50" : "border-slate-200 bg-white hover:border-violet-200"}`}
                    >
                      <p className="font-semibold text-slate-800">{opt.label}</p>
                      <p className="text-slate-500 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermsUserId(null)} disabled={permsSaving}>Cancel</Button>
            <Button data-testid="perms-save" onClick={savePermissions} disabled={!permsForm || permsSaving} className="bg-violet-600 hover:bg-violet-700">
              {permsSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    {children}
  </div>
);

export default UserManagement;
